import { useState, useEffect, useRef, useMemo } from 'react';
import { Tv, Globe, Building2, CircleDot, X } from 'lucide-react';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageHeader } from '@/components/layout/PageContainer';
import { FlagIcon } from '@/components/FlagIcon';
import { ChannelIcon } from '@/pages/media-plan-approval/components/ChannelIcon';
import { CustomSelect } from '@/components/CustomSelect';

// ── SingleSelect (inline dropdown with multi-select support) ─────────────────

interface SingleSelectProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  renderOption?: (option: string) => React.ReactNode;
  icon?: React.ReactNode;
}

const SingleSelect = ({
  label,
  options,
  value,
  onChange,
  renderOption,
  icon,
}: SingleSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Parse comma-separated value into a Set
  const selectedSet = useMemo(() => {
    if (!value) return new Set<string>();
    return new Set(value.split(','));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (option: string) => {
    const newSet = new Set(selectedSet);
    if (newSet.has(option)) {
      newSet.delete(option);
    } else {
      newSet.add(option);
    }
    onChange([...newSet].join(','));
    // Keep dropdown open for multi-select
  };

  const hasValue = selectedSet.size > 0;
  const displayText = !hasValue
    ? label
    : selectedSet.size === 1
      ? [...selectedSet][0]
      : `${label} (${selectedSet.size})`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="px-2.5 py-1 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] flex items-center gap-1.5 whitespace-nowrap shrink-0 min-w-[120px] w-auto transition-all hover:border-slate-400 hover:shadow-sm active:scale-[0.97]"
      >
        {!hasValue && icon}
        <span className={`truncate ${!hasValue ? 'text-slate-400' : 'text-slate-800'}`}>
          {!hasValue && renderOption
            ? displayText
            : hasValue && selectedSet.size === 1 && renderOption
              ? renderOption([...selectedSet][0])
              : displayText}
        </span>
        <svg
          className={`w-3.5 h-3.5 text-slate-400 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {/* All / Clear option */}
          <button
            type="button"
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className={`w-full text-left px-2.5 py-1.5 text-sm transition-colors ${
              !hasValue
                ? 'text-[#0014cc] font-medium bg-slate-50'
                : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            All
          </button>
          {options.map((option) => {
            const isSelected = selectedSet.has(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleToggle(option)}
                className={`w-full text-left px-2.5 py-1.5 text-sm transition-colors ${
                  isSelected
                    ? 'text-[#0014cc] font-medium bg-slate-50'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{renderOption ? renderOption(option) : option}</span>
                  {isSelected && (
                    <svg className="w-4 h-4 text-[#0014cc]" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Header ───────────────────────────────────────────────────────────────────

interface MediaPlanHeaderProps {
  totalPlans: number;
  filteredCount: number;
  isFiltered: boolean;
  filters: {
    channel: string;
    market: string;
    business_unit: string;
    status: string;
  };
  onFilterChange: (filters: {
    channel: string;
    market: string;
    business_unit: string;
    status: string;
  }) => void;
  uniqueChannels: string[];
  uniqueMarkets: string[];
  uniqueBusinessUnits: string[];
  uniqueStatuses: string[];
}

export function MediaPlanHeader({
  totalPlans,
  filteredCount,
  isFiltered,
  filters,
  onFilterChange,
  uniqueChannels,
  uniqueMarkets,
  uniqueBusinessUnits,
  uniqueStatuses,
}: MediaPlanHeaderProps) {
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const suffix = isFiltered
    ? `(${filteredCount}/${totalPlans} Plans)`
    : `(${totalPlans} Plan${totalPlans !== 1 ? 's' : ''})`;

  const actions = (
    <>
      {/* Spacer to push filters right */}
      <div className="flex-1" />

      {/* Channel Filter */}
      <SingleSelect
        label="Channel"
        options={uniqueChannels}
        value={filters.channel}
        onChange={(value) => onFilterChange({ ...filters, channel: value })}
        icon={<Tv className="w-3.5 h-3.5 text-slate-400" />}
        renderOption={(option) => (
          <span className="inline-flex items-center gap-1.5">
            <ChannelIcon channel={option} />
            {option}
          </span>
        )}
      />

      {/* Market Filter */}
      <div className="w-[120px] shrink-0">
        <CustomSelect
          label="Market"
          placeholder="Market"
          options={uniqueMarkets}
          value={filters.market}
          onChange={(value) => onFilterChange({ ...filters, market: value })}
          icon={<Globe className="w-3.5 h-3.5 text-slate-400" />}
          renderOption={(option) => (
            <span className="inline-flex items-center gap-1.5">
              <FlagIcon countryCode={option} className="w-4 h-auto rounded-sm" />
              <span>{option}</span>
            </span>
          )}
          renderMultiDisplay={(selected) =>
            selected.length <= 2 ? (
              <span className="flex items-center gap-1.5">
                {selected.map((m) => (
                  <span key={m} className="inline-flex items-center gap-1">
                    <FlagIcon countryCode={m} className="w-3.5 h-auto rounded-[2px]" />
                    {m}
                  </span>
                ))}
              </span>
            ) : selected.length <= 4 ? (
              <span className="flex items-center justify-center gap-1 w-full">
                {selected.map((m) => (
                  <FlagIcon key={m} countryCode={m} className="w-3.5 h-auto rounded-[2px]" />
                ))}
              </span>
            ) : (
              `${selected.length} markets`
            )
          }
          showLabel={false}
          multi
          searchable
          sortSelectedFirst
        />
      </div>

      {/* Business Unit Filter */}
      <SingleSelect
        label="Unit"
        options={uniqueBusinessUnits}
        value={filters.business_unit}
        onChange={(value) => onFilterChange({ ...filters, business_unit: value })}
        icon={<Building2 className="w-3.5 h-3.5 text-slate-400" />}
      />

      {/* Status Filter */}
      <SingleSelect
        label="Status"
        options={uniqueStatuses}
        value={filters.status}
        onChange={(value) => onFilterChange({ ...filters, status: value })}
        icon={<CircleDot className="w-3.5 h-3.5 text-slate-400" />}
      />

      {/* Clear filters */}
      <button
        onClick={
          activeFilterCount > 0
            ? () => onFilterChange({ channel: '', market: '', business_unit: '', status: '' })
            : undefined
        }
        disabled={activeFilterCount === 0}
        title="Clear filters"
        className={`h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg border transition-all ${
          activeFilterCount > 0
            ? 'bg-[#0014cc] border-[#0014cc] text-white hover:bg-[#0014cc]/90 cursor-pointer'
            : 'border-slate-200 text-slate-300 cursor-not-allowed'
        }`}
      >
        <X className="w-4 h-4" />
      </button>
    </>
  );

  return (
    <PageHeader>
      <Breadcrumb actions={actions} suffix={suffix} />
    </PageHeader>
  );
}
